import React, { useEffect, useState } from "react";
import { api, formatDate, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";
import { ClipboardList, Image as ImageIcon } from "lucide-react";

export default function MitraHistory() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/mitra/stock-opname-history").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/mitra/stock-opname-history/${id}`);
      setDetail(data);
    } catch (e) { console.error(e); }
  };

  const tf = useTableFilter(rows, {
    searchKeys: ["opname_date", "notes"],
    defaultSort: { key: "opname_date", dir: "desc" },
  });

  return (
    <>
      <PageHeader title="Riwayat Opname" subtitle="Kesesuaian stok yang pernah Anda laporkan" testId="page-mitra-history"/>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b"><TableToolbar tf={tf} placeholder="Cari tanggal / catatan..."/></div>
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="w-10">#</TableHead>
            <SortHeader tf={tf} col="opname_date">Tanggal Opname</SortHeader>
            <SortHeader tf={tf} col="date">Waktu Submit</SortHeader>
            <TableHead>Item Diinput</TableHead>
            <TableHead className="text-right">Total Selisih</TableHead>
            <TableHead>Status Adjustment</TableHead>
            <TableHead className="w-20"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tf.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada riwayat opname.</TableCell></TableRow>}
            {tf.rows.map((r, idx) => {
              const totalVar = (r.items || []).reduce((s, it) => s + Number(it.variance || 0), 0);
              const hasIssue = (r.items || []).some((it) => Number(it.variance || 0) !== 0);
              const status = r.adjustment_status || "none";
              const statusBadge = {
                none: <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Sesuai</Badge>,
                pending: <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Menunggu Supplier</Badge>,
                approved: <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Disetujui — Stok Disinkronkan</Badge>,
                partial: <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]">Parsial</Badge>,
                rejected: <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">Ditolak — Stok Sistem Tetap</Badge>,
              }[status];
              return (
                <TableRow key={r.id} className="table-row-hover">
                  <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                  <TableCell className="font-mono-num font-semibold">{formatDate(r.opname_date)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDateTime(r.date)}</TableCell>
                  <TableCell>{(r.items || []).length} produk</TableCell>
                  <TableCell className={`text-right font-mono-num font-semibold ${totalVar === 0 ? "text-slate-400" : totalVar > 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {totalVar === 0 ? "0" : (totalVar > 0 ? "+" : "") + totalVar}
                  </TableCell>
                  <TableCell>{statusBadge}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(r.id)} data-testid={`btn-detail-${r.id}`}>Detail</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail Opname {detail?.opname_date && formatDate(detail.opname_date)}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">Disubmit oleh <b>{detail.submitted_by_name}</b> pada {formatDateTime(detail.date)}</div>
              {detail.notes && <div className="p-2 bg-slate-50 border rounded text-sm">{detail.notes}</div>}
              {detail.close_memo && (
                <div
                  className="border-l-4 border-l-sky-500 bg-sky-50 p-3 text-sm text-sky-950 whitespace-pre-line"
                  data-testid="opname-close-memo"
                >
                  <b>Memo Penutupan:</b> {detail.close_memo}
                </div>
              )}
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Produk</TableHead>
                  <TableHead className="text-right">Sistem</TableHead>
                  <TableHead className="text-right">Fisik</TableHead>
                  <TableHead className="text-right">Selisih</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(detail.items || []).map((it, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{it.product_name}</TableCell>
                      <TableCell className="text-right font-mono-num">{it.system_qty}</TableCell>
                      <TableCell className="text-right font-mono-num">{it.physical_qty}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${Number(it.variance) === 0 ? "text-slate-400" : Number(it.variance) > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {Number(it.variance) > 0 ? "+" : ""}{it.variance}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {detail.photo && (
                <div>
                  <div className="text-xs font-semibold mb-2 flex items-center gap-1"><ImageIcon className="w-3 h-3"/> Foto Fisik:</div>
                  <img src={detail.photo} alt="opname" className="max-w-full rounded border"/>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
